import React, { useMemo } from "react"

import { createBrowserRouter, RouterProvider } from "react-router-dom"

import { ShareLookbookModal, ShareLookbookModalProps } from "../Lookbooks/ShareLookbookModal"
import { ProductDetailModal } from "../ProductDetailModal"
import { DashboardLookbooksIndex, DashboardLookbooksIndexProps, Lookbook } from "./DashboardLookbooksIndex"
import { DashboardLookbooksShow, DashboardLookbooksShowProps, ProductLookbookItem } from "./DashboardLookbooksShow"
import { Bee } from "~/src/components/BeeKit"
import { ErrorPage } from "~/src/components/ErrorPage"
import { appClient } from "~/src/lib/appClients"

export const basename = "/dashboard/lookbooks"

const createRouter = () =>
  createBrowserRouter([
    {
      path: basename,
      element: <DashboardLookbooksIndex />,
      hydrateFallbackElement: <></>,
      errorElement: <ErrorPage />,
      async loader({ request }): Promise<DashboardLookbooksIndexProps> {
        const url = new URL(request.url)

        const response = await appClient.get(`${basename}.json`, {
          headers: { Accept: "application/json" },
          params: url.searchParams,
        })

        const searchTerm = url.searchParams.get("search")

        let filteredItems = response.data.items

        if (searchTerm) {
          filteredItems = response.data.items?.filter((item: Lookbook) =>
            item.name?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        }

        return { ...response.data, items: filteredItems }
      },
      children: [
        {
          path: ":id/latest_products",
          async loader({ params }) {
            const { id } = params

            const response = await appClient.get(`${basename}/${id}/latest_products.json`, {
              headers: { Accept: "application/json" },
            })

            return response.data
          },
        },
      ],
    },
    CreateShowRoute(`${basename}/:id`),
    CreateShowRoute("/lookbooks/:id/:uuid"),
    CreateShowRoute("/lookbooks/:id"),
  ])

function CreateShowRoute(path: string) {
  return {
    path,
    element: <DashboardLookbooksShow />,
    hydrateFallbackElement: <></>,
    errorElement: <ErrorPage />,
    async loader({ request, params }): Promise<DashboardLookbooksShowProps> {
      const url = new URL(request.url)
      const { id, uuid } = params

      const path = [basename, id, uuid].filter(Boolean).join("/")

      const response = await appClient.get(`${path}.json`, {
        headers: { Accept: "application/json" },
        params: url.searchParams,
      })

      const searchTerm = url.searchParams.get("search")

      let filteredItems = response.data.items

      if (searchTerm) {
        filteredItems = response.data.items?.filter(
          (item: ProductLookbookItem) =>
            item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }

      return { ...response.data, items: filteredItems }
    },
    children: [
      {
        path: "share",
        element: <ShareLookbookModal />,
        async loader({ params }): Promise<ShareLookbookModalProps> {
          const { id } = params

          const response = await appClient.get(`${basename}/${id}/share.json`, {
            headers: { Accept: "application/json" },
          })
          return response.data
        },
        async action({ params, request }) {
          const { id } = params
          const formData = await request.formData()
          try {
            await appClient.post(`${basename}/${id}/share`, formData, {
              headers: { Accept: "application/json" },
            })
            Bee.toast.success({ message: "Successfully shared this lookbook." })
            return { success: true }
          } catch (error) {
            const errors = error.response?.data?.errors || "There was an error sharing this lookbook. Please try again."
            errors.forEach((err: string) => {
              Bee.toast.error({ message: err })
            })
            return errors
          }
        },
      },
      {
        path: "products/:productId/:id",
        element: <ProductDetailModal />,
        async loader({ params }) {
          const { id, productId } = params

          const response = await appClient.get(`/products/${productId}/${id}`, {
            headers: { Accept: "application/json" },
          })

          return response.data
        },
      },
    ],
  }
}

/**
 * Renders the dashboard catalog routes at `/dashboard/catalog`.
 * @returns The rendered component.
 */
export function DashboardLookbooksController() {
  const router = useMemo(createRouter, [])

  return <RouterProvider router={router} />
}
